// import PropTypes from 'prop-types';
import {
  Component,
  PureComponent,
  Children,
  createElement,
  cloneElement,
} from 'react';
import {defaults} from 'lodash';
import {Positioner} from '@packages/systems/ui/overlay';
import {chainListeners} from '@packages/utilities/component';
import {mergeRefs} from '../utils/mergeRefs';

const closedState = {open: false};
const openState = {open: true};

// Reads the consumer's ref off a React element in a way that's safe on both
// React 18 and React 19.
//
// TODO(SHSVS-8816): Remove this helper after the React 19 upgrade lands —
// `child.props.ref` is the only correct read in React 19.
//
// React 18: refs live on `element.ref` and are stripped out of `element.props`
// by `createElement`. If a consumer passed a ref via JSX, React installs an
// accessor descriptor on `props.ref` that *warns when read* ("`ref` is not a
// prop"). So a naive `el.props.ref || el.ref` would trigger that warning.
//
// React 19: `ref` becomes a regular prop, so `element.props.ref` is the real
// ref. `element.ref` still resolves but is deprecated and logs a one-time
// warning on access — so reading `el.ref` *first* is the thing we want to
// avoid once we cross over.
//
// Strategy: check the property descriptor on `props`. If `ref` is a real own
// data property (React 19 shape), use it. Otherwise — no ref in props or just
// React 18's warning getter — fall back to `element.ref` without touching
// `props.ref` at all. `Object.getOwnPropertyDescriptor` does not invoke the
// getter, so neither warning fires.
//
// Exported only so the dual-shape behavior can be unit-tested directly; do
// not import from outside this file.
export const readChildRef = (element) => {
  const props = element && element.props;
  if (props) {
    const descriptor = Object.getOwnPropertyDescriptor(props, 'ref');
    if (descriptor && 'value' in descriptor) {
      return descriptor.value;
    }
  }
  return element ? element.ref : null;
};

// Returns a ref that writes to both `consumerRef` (the ref a caller put on
// the tooltip's child) and `anchorRef` (injected by `Positioner` so `Locator`
// can measure the anchor). If `consumerRef` is null/undefined, returns
// `anchorRef` directly — no merging needed.
//
// The merged callback's identity must be stable across renders, otherwise
// React detaches and re-attaches the ref on every parent render, briefly
// nulling `consumerRef.current`. We cache the last merged ref on the class
// instance and reuse it when the input refs are unchanged.
const makeRefMerger = () => {
  let lastConsumerRef = null;
  let lastAnchorRef = null;
  let lastMergedRef = null;
  return (consumerRef, anchorRef) => {
    if (!consumerRef) return anchorRef;
    if (
      consumerRef === lastConsumerRef &&
      anchorRef === lastAnchorRef &&
      lastMergedRef
    ) {
      return lastMergedRef;
    }
    lastConsumerRef = consumerRef;
    lastAnchorRef = anchorRef;
    lastMergedRef = mergeRefs([consumerRef, anchorRef]);
    return lastMergedRef;
  };
};

// `tooltipTrigger` lives in a low-level UI package and must not import the
// Designer app or any telemetry SDK directly. `initialize.ts` plugs the real
// reporter in via `setTooltipBugReporter` at app startup; the default no-op
// keeps tests and isolated mounts side-effect free. Mirrors the same DI shape
// as `setLocatorBugReporter` in `@packages/systems/ui/overlay`.
let _reportBrokenChild = (_componentName, _context) => {};

export const setTooltipBugReporter = (fn) => {
  _reportBrokenChild = fn;
};

// React tags `forwardRef` and `memo` element types with these well-known
// symbols on `$$typeof`. `Symbol.for(...)` returns the same realm-global
// symbol React itself uses, so we can identify these types without importing
// a React internal.
const REACT_FORWARD_REF_TYPE = Symbol.for('react.forward_ref');
const REACT_MEMO_TYPE = Symbol.for('react.memo');

// Module-level so dedup spans all `tooltipTrigger` instances. We dedup on the
// (child type, ownerName) pair rather than the child type alone: the whole
// point of `ownerName` is to enumerate every consumer site that renders a
// broken child, so two different parents rendering the same broken type must
// both be reported — keying on type alone would only ever surface the first
// parent to mount in a session. WeakMap keys on the child type so types can be
// GC'd when no longer referenced; the Set tracks which owners we've already
// warned about for that type.
const warnedOwnersByChildType = new WeakMap();

function isRefAcceptingType(type) {
  if (typeof type === 'string') return true;
  if (typeof type === 'object' && type !== null) {
    if (type.$$typeof === REACT_FORWARD_REF_TYPE) return true;
    // memo() doesn't change ref behavior — recurse into the wrapped type so
    // `memo(forwardRef(X))` (and nested combinations) are correctly accepted.
    if (type.$$typeof === REACT_MEMO_TYPE) return isRefAcceptingType(type.type);
  }
  return false;
}

// Resolve a human-readable name from a component "type" value, unwrapping
// `forwardRef`/`memo` wrappers so owners built with those helpers (common in
// this codebase) still attribute to a name instead of falling into the
// undefined bucket. A plain function/class exposes `displayName`/`name`
// directly; a `forwardRef` object carries an optional `displayName` and wraps
// a `render` function; a `memo` object wraps `.type`.
function getComponentTypeName(type) {
  if (!type) return undefined;
  if (typeof type === 'function') {
    return type.displayName || type.name || undefined;
  }
  if (typeof type === 'object') {
    if (type.displayName) return type.displayName;
    if (type.$$typeof === REACT_FORWARD_REF_TYPE) {
      return getComponentTypeName(type.render);
    }
    if (type.$$typeof === REACT_MEMO_TYPE) {
      return getComponentTypeName(type.type);
    }
  }
  return undefined;
}

// Reads React's internal `_owner` off the child element to attribute each
// broken tooltip child to the component that authored the JSX (e.g. which
// component wraps `<View>` in `<TooltipTrigger>`).
//
// IMPORTANT — this is largely a DEV-only signal. `_owner` is set from
// `ReactCurrentOwner.current` at element-creation time, and React 18 only
// assigns `ReactCurrentOwner.current` during render for:
//   - function/forwardRef components: inside a DEV-only block (verified: it
//     sits alongside `setIsRendering`, which is stripped from the production
//     bundle), so in prod `_owner` is null for these owners; and
//   - class components: unconditionally, so `_owner` IS populated in prod.
//
// Net effect in production: `ownerName` resolves only when the owning
// component is a class. For function/forwardRef owners (e.g. `ListDragHandle`),
// and for elements created outside any render or by anonymous components,
// `ownerName` is undefined. In local dev it resolves for all component types.
//
// So treat `ownerName` as a dev-time debugging aid plus partial (class-owner)
// prod attribution — NOT a complete prod consumer map.
function getOwnerName(child) {
  const owner = child && child._owner;
  if (!owner) return undefined;
  return getComponentTypeName(owner.type);
}

function warnNonRefAcceptingChild(child) {
  const {type} = child;
  if (isRefAcceptingType(type)) return;

  const ownerName = getOwnerName(child);

  // Skip primitives — WeakMap keys must be objects/functions. Dedup on the
  // (type, ownerName) pair so each distinct consumer site of a broken type is
  // reported once per session, rather than only the first parent to mount it.
  if (
    typeof type === 'function' ||
    (typeof type === 'object' && type !== null)
  ) {
    let warnedOwners = warnedOwnersByChildType.get(type);
    if (!warnedOwners) {
      warnedOwners = new Set();
      warnedOwnersByChildType.set(type, warnedOwners);
    }
    if (warnedOwners.has(ownerName)) return;
    warnedOwners.add(ownerName);
  }

  const name =
    (typeof type === 'function'
      ? type.displayName || type.name
      : String(type)) || 'Unknown';

  try {
    _reportBrokenChild(name, {
      warningType: 'tooltipTriggerNonRefAcceptingChild',
      ownerName,
    });
  } catch {
    // Never let a reporter failure break rendering.
  }

  // Dev-only — production observability comes from the telemetry call above;
  // we deliberately keep the prod console silent.
  if (process.env.NODE_ENV !== 'production') {
    const ownerSuffix = ownerName ? ` (rendered by <${ownerName}>)` : '';
    console.warn(
      `[tooltipTrigger] Child component <${name}>${ownerSuffix} won't receive the anchor ref — ` +
        'it is not a forwardRef component or a host element. ' +
        'The tooltip will not position correctly relative to its trigger. ' +
        'Wrap the child in React.forwardRef, or use a host element (<div>, <span>) as the direct child.'
    );
  }
}

export default ({delay = 0, hideOnClick = true}) =>
  (BaseComponent) => {
    const baseComponent = (baseComponentProps) =>
      createElement(BaseComponent, baseComponentProps);

    const TooltipTrigger = class extends PureComponent {
      state = closedState;

      getMergedRef = makeRefMerger();

      open = () => {
        this.setState(openState);
      };

      close = () => {
        clearTimeout(this.timeout);
        this.setState(closedState);
      };

      resetTimer = () => {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(this.open, delay);
      };

      handlers = {
        onMouseEnter: this.resetTimer,
        onMouseOver: this.resetTimer,
        onMouseLeave: this.close,
        onWheel: this.close,
        onClick: hideOnClick ? this.close : undefined,
      };

      tooltipRender = ({viewport, anchor, target, targetRef}) => {
        const {content, style, ...rest} = this.props;

        return baseComponent({
          ...rest,
          viewport,
          anchor,
          targetRef,
          style: defaults(
            {
              position: 'absolute',
            },
            target,
            style
          ),
          children: content,
        });
      };

      componentDidMount() {
        warnNonRefAcceptingChild(Children.only(this.props.children));
      }

      componentWillUnmount() {
        this.close();
      }

      render() {
        return createElement(
          Positioner,
          {
            open: this.props.content && this.state.open,
            render: this.tooltipRender,
          },
          (anchorRef) => {
            const onlyChild = Children.only(this.props.children);
            const child = chainListeners(this.handlers, onlyChild);
            return cloneElement(child, {
              ref: this.getMergedRef(readChildRef(child), anchorRef),
            });
          }
        );
      }
    };

    TooltipTrigger.displayName = `tooltipTrigger(${
      BaseComponent.displayName || BaseComponent.name
    })`;

    // TooltipTrigger.propTypes = {
    //   content: PropTypes.node,
    //   children: PropTypes.element.isRequired,
    // };

    return TooltipTrigger;
  };

export const controlledTooltipTrigger = (BaseComponent) => {
  const baseComponent = (baseComponentProps) =>
    createElement(BaseComponent, baseComponentProps);

  const ControlledTooltipTrigger = class extends Component {
    // HACK: I'd much prefer we don't use state at all since this is a controlled component,
    // but there were several issues inside KeyboardContext and Overlay that occur when the tooltip
    // is `open` by default, presumably before any layers exist yet.
    //
    // By always starting closed and only opening by triggering state change after mounting,
    // we mimic a proper controlled API and avoid these timing issues.
    state = {
      mounted: false,
    };

    getMergedRef = makeRefMerger();

    componentDidMount() {
      this.setState({mounted: true});
      warnNonRefAcceptingChild(Children.only(this.props.children));
    }

    tooltipRender = ({viewport, anchor, target, targetRef}) => {
      const {content, style, ...rest} = this.props;

      return baseComponent({
        ...rest,
        viewport,
        anchor,
        targetRef,
        style: defaults(
          {
            position: 'absolute',
          },
          target,
          style
        ),
        children: content,
      });
    };

    render() {
      return createElement(
        Positioner,
        {
          open: this.state.mounted ? this.props.open : false,
          render: this.tooltipRender,
        },
        (anchorRef) => {
          const child = Children.only(this.props.children);
          return cloneElement(child, {
            ref: this.getMergedRef(readChildRef(child), anchorRef),
          });
        }
      );
    }
  };

  ControlledTooltipTrigger.displayName = `controlledTooltipTrigger(${
    BaseComponent.displayName || BaseComponent.name
  })`;

  // ControlledTooltipTrigger.propTypes = {
  //   open: PropTypes.bool,
  //   content: PropTypes.node,
  //   children: PropTypes.element.isRequired,
  // };

  return ControlledTooltipTrigger;
};
